// src/utils/form/formQuestions.js

import { PROFILE_MEASUREMENT_LIMITS } from "../validation/controlledInputs";

const FORMER_SMOKING_STATUSES = new Set(["former", "ecigaretteFormer"]);

export const questions = [
  {
    id: "height",
    titleKey: "height.title",
    fieldType: "number",
    inputType: "text",
    required: true,
    min: PROFILE_MEASUREMENT_LIMITS.height.min,
    max: PROFILE_MEASUREMENT_LIMITS.height.max,
    repeatable: true,
    group: "demographics",
  },
  {
    id: "weight",
    titleKey: "weight.title",
    fieldType: "number",
    required: true,
    min: PROFILE_MEASUREMENT_LIMITS.weight.min,
    max: PROFILE_MEASUREMENT_LIMITS.weight.max,
    repeatable: true,
    group: "demographics",
  },
  {
    id: "chestCircumference",
    titleKey: "chestCircumference.title",
    fieldType: "number",
    required: false,
    min: PROFILE_MEASUREMENT_LIMITS.chestCircumference.min,
    max: PROFILE_MEASUREMENT_LIMITS.chestCircumference.max,
    repeatable: true,
    group: "demographics",
    descriptionKey: "chestCircumference.desc",
    resourceUrl:
      "https://firebasestorage.googleapis.com/v0/b/pdx-web-v2.firebasestorage.app/o/metadata_form_assets%2Fchest_measurement.PNG?alt=media&token=8266715a-b1a8-4a64-a723-65b5eda7a092",
    description:
      "Measure your chest circumference at the topmost part of the chest - directly under the underarms and above the bust - keeping the tape level around your torso and snug without compressing the chest.",
  },
  {
    id: "dateOfBirth",
    titleKey: "dateOfBirth.title",
    fieldType: "date",
    required: true,
    repeatable: false,
    group: "demographics",
  },
  {
    id: "sexAtBirth",
    titleKey: "sexAtBirth.title",
    fieldType: "radio",
    required: true,
    options: [
      { value: "Female", labelKey: "sexAtBirth.opt.female" },
      { value: "Male", labelKey: "sexAtBirth.opt.male" },
    ],
    repeatable: false,
    group: "demographics",
  },
  {
    id: "ethnicity",
    titleKey: "ethnicity.title",
    fieldType: "select",
    required: true,
    options: [
      { value: "White - British", labelKey: "ethnicity.opt.whiteBritish" },
      { value: "White - Irish", labelKey: "ethnicity.opt.whiteIrish" },
      {
        value: "White - Any other White background",
        labelKey: "ethnicity.opt.whiteOther",
      },
      {
        value: "Mixed - White and Black Caribbean",
        labelKey: "ethnicity.opt.mixedWhiteBlackCaribbean",
      },
      {
        value: "Mixed - White and Black African",
        labelKey: "ethnicity.opt.mixedWhiteBlackAfrican",
      },
      {
        value: "Mixed - White and Asian",
        labelKey: "ethnicity.opt.mixedWhiteAsian",
      },
      {
        value: "Mixed - Any other mixed background",
        labelKey: "ethnicity.opt.mixedOther",
      },
      {
        value: "Asian or Asian British - Indian",
        labelKey: "ethnicity.opt.asianIndian",
      },
      {
        value: "Asian or Asian British - Pakistani",
        labelKey: "ethnicity.opt.asianPakistani",
      },
      {
        value: "Asian or Asian British - Bangladeshi",
        labelKey: "ethnicity.opt.asianBangladeshi",
      },
      {
        value: "Asian or Asian British - Any other Asian background",
        labelKey: "ethnicity.opt.asianOther",
      },
      {
        value: "Black or Black British - Caribbean",
        labelKey: "ethnicity.opt.blackCaribbean",
      },
      {
        value: "Black or Black British - African",
        labelKey: "ethnicity.opt.blackAfrican",
      },
      {
        value: "Black or Black British - Any other Black background",
        labelKey: "ethnicity.opt.blackOther",
      },
      {
        value: "Other Ethnic Groups - Chinese",
        labelKey: "ethnicity.opt.chinese",
      },
      {
        value: "Other Ethnic Groups - Any other ethnic group",
        labelKey: "ethnicity.opt.otherEthnicGroup",
      },
      { value: "Not stated", labelKey: "ethnicity.opt.notStated" },
      { value: "Not known", labelKey: "ethnicity.opt.notKnown" },
    ],
    freeTextFieldId: "ethnicityOther",
    freeTextTriggerValue: "Other Ethnic Groups - Any other ethnic group",
    freeTextTitleKey: "ethnicity.otherTitle",
    freeTextPlaceholderKey: "ethnicity.otherPlaceholder",
    includeCurrentValueAsLegacyOption: true,
    repeatable: false,
    group: "demographics",
  },
  {
    id: "respiratoryConditions",
    titleKey: "respiratoryConditions.title",
    fieldType: "checkbox-group",
    required: true,
    repeatable: false,
    options: [
      { value: "none", labelKey: "respiratoryConditions.opt.none" },
      { value: "ild", labelKey: "respiratoryConditions.opt.ild" },
      { value: "copd", labelKey: "respiratoryConditions.opt.copd" },
      { value: "asthma", labelKey: "respiratoryConditions.opt.asthma" },
      {
        value: "other",
        labelKey: "respiratoryConditions.opt.other",
      },
    ],
    freeTextFieldId: "respiratoryConditionsOther",
    freeTextTriggerValue: "other",
    freeTextTitleKey: "respiratoryConditions.otherTitle",
    freeTextPlaceholderKey: "respiratoryConditions.otherPlaceholder",
    group: "healthHistory",
  },

  // Smoking Questions
  {
    id: "smoking",
    titleKey: "smoking.title",
    fieldType: "radio",
    required: true,
    options: [
      { value: "never", labelKey: "smoking.opt.never" },
      { value: "former", labelKey: "smoking.opt.former" },
      {
        value: "ecigaretteFormer",
        labelKey: "smoking.opt.ecigaretteFormer",
      },
      { value: "current", labelKey: "smoking.opt.current" },
      { value: "ecigarette", labelKey: "smoking.opt.ecigarette" },
    ],
    repeatable: true,
    group: "healthHistory",
  },

  // Ex-smoker follow-up questions:
  {
    id: "yearsSinceQuit",
    titleKey: "yearsSinceQuit.title",
    fieldType: "select",
    required: true,
    options: [
      { value: "lessThan1month", labelKey: "yearsSinceQuit.opt.lt1m" },
      { value: "1-6months", labelKey: "yearsSinceQuit.opt.1_6m" },
      { value: "6-12months", labelKey: "yearsSinceQuit.opt.6_12m" },
      { value: "1-5years", labelKey: "yearsSinceQuit.opt.1_5y" },
      { value: "moreThan5years", labelKey: "yearsSinceQuit.opt.gt5y" },
    ],
    condition: (data) => FORMER_SMOKING_STATUSES.has(data.smoking),
    repeatable: true,
    group: "healthHistory",
  },
  {
    id: "exQuitEffect",
    titleKey: "exQuitEffect.title",
    fieldType: "range",
    required: false,
    min: 0,
    max: 10,
    step: 1,
    rangeDescriptionKey: "exQuitEffect.desc",
    condition: (data) => FORMER_SMOKING_STATUSES.has(data.smoking),
    repeatable: true,
    group: "healthHistory",
  },
];
